import React, { useState } from 'react';
import { AlertTriangle, Droplets } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Base sur les plans de rehydratation orale de l'OMS (Plan A / B / C)
const SEVERE_SIGNS = [
  { key: 'leth', tKey: 'dehydration_sign_lethargy' },
  { key: 'boit', tKey: 'dehydration_sign_no_drink' },
  { key: 'yeux', tKey: 'dehydration_sign_eyes_severe' },
  { key: 'pli', tKey: 'dehydration_sign_skin_severe' },
];

const SOME_SIGNS = [
  { key: 'agite', tKey: 'dehydration_sign_agitated' },
  { key: 'soif', tKey: 'dehydration_sign_thirsty' },
  { key: 'yeuxL', tKey: 'dehydration_sign_eyes_some' },
  { key: 'pliL', tKey: 'dehydration_sign_skin_some' },
];

const DehydrationCalculator = () => {
  const { t } = useLanguage();
  const [weight, setWeight] = useState('');
  const [ageYears, setAgeYears] = useState('');
  const [severe, setSevere] = useState({});
  const [some, setSome] = useState({});

  const toggleSevere = (key) => setSevere((c) => ({ ...c, [key]: !c[key] }));
  const toggleSome = (key) => setSome((c) => ({ ...c, [key]: !c[key] }));

  const severeCount = Object.values(severe).filter(Boolean).length;
  const someCount = Object.values(some).filter(Boolean).length;

  const w = parseFloat(weight);
  const age = parseFloat(ageYears);

  let level = 'aucune';
  if (severeCount >= 2) level = 'severe';
  else if (someCount >= 2) level = 'moderee';

  const planBVolume = w > 0 ? Math.round(w * 75) : null;

  let planAVolume = null;
  if (!Number.isNaN(age)) {
    planAVolume = age < 2 ? t('dehydration_planA_under2') : t('dehydration_planA_over2');
  }

  const anyChecked = severeCount > 0 || someCount > 0;

  return (
    <ToolPageLayout
      title={t('dehydration_title')}
      subtitle={t('dehydration_subtitle')}
      specialty="pediatre"
    >
      <div className="bg-[#FEE2E2] border border-[#EF4444]/30 rounded-2xl p-4 flex gap-3 mb-8">
        <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
        <p className="text-sm text-[#991B1B]">
          {t('dehydration_warning')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('bmi_weight_label')}</Label>
          <Input type="number" min="1" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={t('dehydration_weight_placeholder')} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('dehydration_age_years_label')}</Label>
          <Input type="number" min="0" step="0.1" value={ageYears} onChange={(e) => setAgeYears(e.target.value)} placeholder={t('dehydration_age_years_placeholder')} className="rounded-xl" />
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="font-semibold text-[#0F172A] mb-3">{t('dehydration_severe_heading')}</h3>
          <div className="space-y-2">
            {SEVERE_SIGNS.map((s) => (
              <label key={s.key} className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 cursor-pointer hover:border-[#EF4444]/40">
                <input type="checkbox" checked={!!severe[s.key]} onChange={() => toggleSevere(s.key)} className="w-4 h-4 accent-[#EF4444]" />
                <span className="text-sm text-[#0F172A]">{t(s.tKey)}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-[#0F172A] mb-3">{t('dehydration_moderate_heading')}</h3>
          <div className="space-y-2">
            {SOME_SIGNS.map((s) => (
              <label key={s.key} className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 cursor-pointer hover:border-[#F59E0B]/40">
                <input type="checkbox" checked={!!some[s.key]} onChange={() => toggleSome(s.key)} className="w-4 h-4 accent-[#F59E0B]" />
                <span className="text-sm text-[#0F172A]">{t(s.tKey)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {level === 'severe' && (
        <div className="mt-8 rounded-2xl p-6 border bg-[#FEE2E2] border-[#EF4444]/30 flex gap-4">
          <AlertTriangle className="w-6 h-6 text-[#EF4444] shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-[#991B1B]">{t('dehydration_severe_result_title')}</p>
            <p className="text-sm text-[#991B1B] mt-1">
              {t('dehydration_severe_result_text')}
            </p>
          </div>
        </div>
      )}

      {level === 'moderee' && (
        <div className="mt-8 rounded-2xl p-6 border bg-[#FEF3C7] border-[#F59E0B]/30">
          <p className="font-bold text-[#92400E] flex items-center gap-2">
            <Droplets className="w-5 h-5" /> {t('dehydration_moderate_result_title')}
          </p>
          <p className="text-sm text-[#92400E] mt-2">
            {t('dehydration_moderate_result_text')}
          </p>
          {planBVolume && (
            <p className="text-lg font-bold text-[#92400E] mt-3">≈ {planBVolume} ml {t('dehydration_over_4h')}</p>
          )}
          <p className="text-sm text-[#92400E] mt-2">
            {t('dehydration_reassess')}
          </p>
        </div>
      )}

      {level === 'aucune' && !anyChecked && (
        <div className="mt-8 rounded-2xl p-6 border bg-[#F0FDF4] border-[#10B981]/30">
          <p className="font-bold text-[#065F46]">{t('dehydration_none_title')}</p>
          <p className="text-sm text-[#065F46] mt-2">
            {t('dehydration_none_text')}
          </p>
          {planAVolume && (
            <p className="text-sm font-semibold text-[#065F46] mt-2">
              {age < 2 ? t('dehydration_under2') : t('dehydration_over2')} : {planAVolume} {t('dehydration_after_stool')}
            </p>
          )}
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('dehydration_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default DehydrationCalculator;
